import { AddressSuggestion as ApiAddressSuggestion } from '@/features/api/geo/types'
import { CustomerLookupQuery } from '@/features/graphql/generated/graphql'
import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import {
    FormDefaultValues,
    FormOutput,
    PhoneInput,
} from '@/features/pos/components/CustomerProfile/form/types'
import {
    AddressSuggestion,
    CustomerLookupCustomer,
    CustomerLookupSaveInput,
} from '@/features/pos/components/CustomerProfile/types'
import { CustomerInput } from '@/features/pos/components/OrderFinalization/form/types'
import CountryList from 'country-list-with-dial-code-and-flag'
import parsePhoneNumber from 'libphonenumber-js'

type CustomerLookupApiCustomer = CustomerLookupQuery['customers'][number]

export const transformAddressSuggestion = (
    addressSuggestion: ApiAddressSuggestion
): AddressSuggestion => ({
    address: addressSuggestion.formattedAddress,
    isWhole: addressSuggestion.isWholeAddress,
})

export const formatCustomerAddress = (
    address: CustomerLookupCustomer['addresses'][number]
): string => [address.street, address.zip, address.city].filter(Boolean).join(', ')

export const transformCustomerLookupCustomer = (
    customer: CustomerLookupApiCustomer
): CustomerLookupCustomer => ({
    addresses: customer.addresses.map((address) => ({
        city: address.city ?? null,
        lat: address.lat ?? null,
        lng: address.lng ?? null,
        street: address.street ?? null,
        uuid: address.uuid,
        zip: address.zip ?? null,
    })),
    name: customer.name ?? null,
    names: customer.names ?? [],
    phoneNumber: customer.phoneNumber ?? null,
    phoneNumbers: customer.phoneNumbers ?? [],
    uuid: customer.uuid,
})

export const transformCustomerToProfileFormDefaultValues = (
    customer?: CustomerInput
): FormDefaultValues => ({
    ...(customer?.phone && {
        phone: {
            dialCode: '',
            number: customer.phone,
        },
    }),
    name: customer?.name ?? '',
    ...(customer?.address && {
        address: {
            address: customer.address.address,
            coordinates: customer.address.coordinates,
            isValid: true,
            zone: customer.address.zone,
        },
    }),
})

export const transformPhoneSearchValueToProfilePhone = (
    phoneNumber: string,
    defaultCountryCode: string
): PhoneInput => {
    const country = CountryList.findOneByCountryCode(defaultCountryCode)

    return {
        flag: country?.flag ?? '',
        dialCode: phoneNumber.trimStart().startsWith('+') ? '' : (country?.dialCode ?? ''),
        number: phoneNumber,
    }
}

export const transformProfileFormToCustomerLookupSaveInput = (
    form: FormOutput,
    deliveryTypeCategory: DeliveryTypeCategory
): CustomerLookupSaveInput => {
    const rawPhone = form.phone.dialCode + form.phone.number
    const phone = parsePhoneNumber(rawPhone, { extract: false })?.number ?? rawPhone

    return {
        deliveryTypeCategory,
        name: form.name.trim(),
        phone,
        ...(deliveryTypeCategory === DeliveryTypeCategory.Delivery &&
            'address' in form && {
                address: {
                    address: form.address.address,
                    coordinates: form.address.coordinates,
                    zone: form.address.zone,
                },
            }),
    }
}
